import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import _ from "lodash";
import { useQueryState } from "react-router-use-location-state";
import {
  useLatestWorkflowDefs,
  useWorkflowVersionsWithEnabled,
} from "../../data/workflow";
import Header from "./Header";
import sharedStyles from "../styles";
import { Helmet } from "react-helmet";
import { IconButton, Snackbar } from "@material-ui/core";
import { Tooltip } from "@material-ui/core";
import PlayArrowOutlinedIcon from "@material-ui/icons/PlayArrowOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import {
  deleteEntireWorkflowApi,
  deleteWorkflowApi,
  getCategories,
} from "../../data/services/API";
import ConfirmChoiceDialog from "../../components/ConfirmChoiceDialog";
import { useHistory } from "react-router-dom";
import CloseIcon from "@material-ui/icons/Close";
import executionsStyles from "./../executions/executionsStyles";
import AgGridTable from "../../components/AgGridTable";
import CustomPagination from "../executions/CustomPagination";
import { Alert, AlertTitle } from "@material-ui/lab";
import ActionButton from "../../components/ActionButton";

import CustomLoadingOverlay from "../../components/CustomLoadingOverlay";
import {
  useCellRenderer,
  tooltipGetters,
} from "../../utils/cellRendererFactory";

const useStyles = makeStyles({
  ...executionsStyles,
  ...sharedStyles,

  inputWrapper: {
    display: "flex",
    flexDirection: "column",
  },
  inputLabel: {
    marginBottom: 1,
    marginLeft: 2,
    fontSize: 10,
    fontWeight: 600,
    color: "#000",
  },
  subheader: {
    minHeight: "150px",
    alignItems: "center",
    justifyContent: "space-evenly",
    display: "flex",
    width: "100%",
    flexWrap: "wrap",
    backgroundColor: "#f4f6ff",
  },
});

export default function WorkflowDefinitions(props) {
  const permissions = props.permissions;

  const classes = useStyles();

  const [page, setPage] = useQueryState("page", 1);
  const [rowsPerPage, setRowsPerPage] = useQueryState("rowsPerPage", 15);
  const [selectedWorkflowName, setSelectedWorkflowName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [, setCategories] = useState([]);
  const [columnFilters, setColumnFilters] = useState({});

  const create_access =
    permissions?.workflows?.actions?.create_workflow === "True";

  const [clickedDeleteBtn, setClickedDeleteBtn] = useState({
    row: null,
    openDialog: false,
    header: "",
    message: "",
  });
  const [messageSnackbar, setMessageSnackbar] = useState(null);

  const history = useHistory();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getCategories();
        setCategories(response.data);
      } catch (error) {}
    };

    fetchData();
  }, []);

  const [workflowToBeDeleted, setworkflowTobedeleted] = useState("");
  const [workflowVersions, setWorkflowversions] = useState([]);

  const { data, totalCount, isFetching, error, refetch } =
    useLatestWorkflowDefs(
      page,
      rowsPerPage,
      selectedWorkflowName ||
        (columnFilters.workflowName ? columnFilters.workflowName : ""),
      selectedCategory,
      {
        enabled: true,
      }
    );

  const workflows = useMemo(() => {
    if (data) {
      const unique = new Map();
      for (let workflowDef of data) {
        if (!unique.has(workflowDef.name)) {
          unique.set(workflowDef.name, workflowDef);
        } else if (unique.get(workflowDef.name).version < workflowDef.version) {
          unique.set(workflowDef.name, workflowDef);
        }
      }
      var uniqueValues = Array.from(unique.values());
      var sortedUniqueValues = uniqueValues.sort(
        (b, a) => a.createTime - b.createTime
      );
      return sortedUniqueValues;
    }
    return [];
  }, [data]);

  const {
    data: wfVersions,
    refetch: refetchwfVersions,
    isFetching: isFetchingwfVersions,
  } = useWorkflowVersionsWithEnabled(workflowToBeDeleted, {
    enabled: !!workflowToBeDeleted,
  });

  useEffect(() => {
    if (workflowToBeDeleted) {
      refetchwfVersions();
    }
  }, [workflowToBeDeleted, refetchwfVersions]);

  useEffect(() => {
    if (
      wfVersions !== null &&
      wfVersions !== undefined &&
      workflowToBeDeleted
    ) {
      setWorkflowversions(wfVersions);
    }
  }, [wfVersions, workflowToBeDeleted]);

  function runWorkflow(row) {
    history.push("/workbench/" + row.name + "/" + row.version);
  }

  async function deleteWorkflow(
    row,
    deleteEntireWorkflow = false,
    versionNumber
  ) {
    if (row) {
      try {
        const response = deleteEntireWorkflow
          ? await deleteEntireWorkflowApi(row.name)
          : await deleteWorkflowApi(row.name, versionNumber);
        if (response?.status === 200) {
          setMessageSnackbar({
            message: response?.data
              ? response.data
              : "Workflow deleted successfully",
            severity: "success",
            open: true,
            autoHideDuration: 3000,
            onClose: () => {
              setMessageSnackbar(null);
            },
          });
          setClickedDeleteBtn({
            row: null,
            openDialog: false,
            header: "",
            message: "",
          });
          refetch();
          refetchwfVersions();
          setworkflowTobedeleted("");
          setWorkflowversions([]);
        } else {
          setMessageSnackbar({
            message: "Something went wrong.",
            severity: "error",
            open: true,
            autoHideDuration: 3000,
            onClose: () => {
              setMessageSnackbar(null);
            },
          });
          setClickedDeleteBtn({
            row: null,
            openDialog: false,
            header: "",
            message: "",
          });
        }
      } catch (err) {
        setMessageSnackbar({
          message: "Something went wrong.",
          severity: "error",
          open: true,
          autoHideDuration: 3000,
          onClose: () => {
            setMessageSnackbar(null);
          },
        });
        setClickedDeleteBtn({
          row: null,
          openDialog: false,
          header: "",
          message: "",
        });
      }
    }
  }

  const handleWorkflowDeleteBtnClick = useCallback((row) => {
    setworkflowTobedeleted(row.name);
    setClickedDeleteBtn({
      row: row,
      openDialog: true,
      header: "Delete Workflow",
      message: `This action will delete version the selected version of workflow ${row.name}.`,
    });
  }, []);

  const actionsRenderer = useCallback(
    (params) => {
      return (
        <div className="flex-row workflow-action-btns">
          {permissions?.workflows?.actions?.run === "True" ? (
            <Tooltip title="Run">
              <IconButton
                onClick={() => runWorkflow(params.data)}
                color={"default"}
                className="run-icon primary"
              >
                <PlayArrowOutlinedIcon />
              </IconButton>
            </Tooltip>
          ) : null}
          {permissions?.workflows?.actions?.delete === "True" ? (
            <Tooltip title="Delete">
              <IconButton
                onClick={() => handleWorkflowDeleteBtnClick(params.data)}
                color={"default"}
                className="warn"
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      );
    },
    [permissions, handleWorkflowDeleteBtnClick]
  );

  const ConformationDialog = useMemo(() => {
    const { openDialog, header, message, row } = clickedDeleteBtn;
    if (openDialog) {
      return (
        <ConfirmChoiceDialog
          header={header}
          message={message}
          versions={workflowVersions ? workflowVersions : []}
          loading={isFetchingwfVersions}
          handleConfirmationValue={(confirm, deleteAll, versionNumber) => {
            if (confirm) {
              deleteWorkflow(row, deleteAll, versionNumber);
            } else {
              setworkflowTobedeleted("");
              setClickedDeleteBtn({
                row: null,
                openDialog: false,
                header: "",
                message: "",
              });
              setWorkflowversions([]);
            }
          }}
          open={openDialog}
          confirmText="Delete"
          dialogType="DeleteWorkflow"
          additionalCheckBox={"Delete All Versions"}
          additionalCheckBoxAction={(checked) => {
            if (checked) {
              setClickedDeleteBtn({
                ...clickedDeleteBtn,
                message:
                  "WARNING: This action will delete all the versions of " +
                  row.name,
              });
            } else {
              setClickedDeleteBtn({
                ...clickedDeleteBtn,
                message:
                  "This action will delete the selected version " +
                  " of workflow " +
                  row.name +
                  ".",
              });
            }
          }}
        />
      );
    } else {
      return null;
    }
  }, [clickedDeleteBtn, workflowVersions, isFetchingwfVersions]);

  const simpleRenderer = useCellRenderer("simple", true);
  const linkRenderer = useCellRenderer("link", true);
  const timeRenderer = useCellRenderer("timestamp", true);
  const jsonRenderer = useCellRenderer("json", true);
  const taskTypesRenderer = useCellRenderer("taskTypes", true);
  const taskCountRenderer = useCellRenderer("taskCount", true);
  const executionsRenderer = useCellRenderer("executions", true);

  const columnDefs = useMemo(
    () => [
      {
        field: "workflowName",
        headerName: "Name",
        cellRenderer: linkRenderer,
        tooltipValueGetter: tooltipGetters.simple,
        width: 300,
        minWidth: 200,
        flex: 1,
        filterable: true,
        filterType: "workflowDefinitionSearch",
      },
      {
        field: "category",
        headerName: "Type",
        cellRenderer: simpleRenderer,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "description",
        headerName: "Description",
        cellRenderer: simpleRenderer,
        tooltipValueGetter: tooltipGetters.simple,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "createTime",
        headerName: "Create Time",
        cellRenderer: timeRenderer,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "version",
        headerName: "Latest Version",
        cellRenderer: simpleRenderer,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "schemaVersion",
        headerName: "Schema Version",
        cellRenderer: simpleRenderer,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "restartable",
        headerName: "Restartable",
        cellRenderer: simpleRenderer,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "workflowStatusListenerEnabled",
        headerName: "Workflow Status Listener Enabled",
        cellRenderer: simpleRenderer,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "ownerEmail",
        headerName: "Owner Email",
        cellRenderer: simpleRenderer,
        tooltipValueGetter: tooltipGetters.simple,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "inputParameters",
        headerName: "Input Parameters",
        cellRenderer: jsonRenderer,
        tooltipValueGetter: tooltipGetters.json,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "outputParameters",
        headerName: "Output Parameters",
        cellRenderer: jsonRenderer,
        tooltipValueGetter: tooltipGetters.json,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "timeoutPolicy",
        headerName: "Timeout Policy",
        cellRenderer: simpleRenderer,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "timeoutSeconds",
        headerName: "Timeout Seconds",
        cellRenderer: simpleRenderer,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "ttlSeconds",
        headerName: "Time to Live",
        cellRenderer: simpleRenderer,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "archive",
        headerName: "Archive",
        cellRenderer: simpleRenderer,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "task_types",
        headerName: "Task Types",
        cellRenderer: taskTypesRenderer,
        tooltipValueGetter: tooltipGetters.taskTypes,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "tasks",
        headerName: "Tasks",
        cellRenderer: taskCountRenderer,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "name",
        headerName: "Executions",
        cellRenderer: executionsRenderer,
        width: 300,
        minWidth: 200,
        flex: 1,
      },
      ...(permissions?.workflows?.actions &&
      (permissions?.workflows?.actions.run === "True" ||
        permissions?.workflows?.actions.delete === "True")
        ? [
            {
              headerName: "Actions",
              field: "actions",
              cellRenderer: actionsRenderer,
              width: 300,
              minWidth: 200,
              flex: 1,
            },
          ]
        : []),
    ],
    [
      simpleRenderer,
      linkRenderer,
      timeRenderer,
      taskTypesRenderer,
      taskCountRenderer,
      jsonRenderer,
      executionsRenderer,
      tooltipGetters,
      permissions,
      actionsRenderer,
    ]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
    }),
    []
  );

  const defaultVisibleColumns = useMemo(
    () => [
      "workflowName",
      "category",
      "description",
      "createTime",
      "version",
      "ownerEmail",
      "tasks",
      "name",
      "actions",
    ],
    []
  );

  const handlePageChange = useCallback(
    (newPage) => {
      setPage(newPage + 1);
    },
    [setPage]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize) => {
      setRowsPerPage(newPageSize);
      setPage(1);
    },
    [setRowsPerPage, setPage]
  );

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleColumnFilterChanged = useCallback(
    (fieldName, filterValue) => {
      setColumnFilters((prev) => {
        const newFilters = { ...prev };
        if (filterValue) {
          newFilters[fieldName] = filterValue;
        } else {
          delete newFilters[fieldName];
        }
        return newFilters;
      });

      if (fieldName === "workflowName") {
        // Handle workflowDefinitionSearch filter (object with workflowName, category, and isRegex)
        if (
          filterValue &&
          typeof filterValue === "object" &&
          filterValue.workflowName
        ) {
          if (filterValue.isRegex) {
            // For regex, set name to the pattern and category to empty
            setSelectedWorkflowName(filterValue.workflowName);
            setSelectedCategory("");
          } else {
            // Normal search
            setSelectedWorkflowName(filterValue.workflowName);
            setSelectedCategory(filterValue.category || "");
          }
        }
        // Handle legacy workflowName filter (array or string)
        else if (Array.isArray(filterValue) && filterValue.length > 0) {
          setSelectedWorkflowName(filterValue[0]);
        } else if (typeof filterValue === "string") {
          setSelectedWorkflowName(filterValue);
        } else {
          setSelectedWorkflowName("");
          setSelectedCategory("");
        }
      }

      if (fieldName === "category") {
        setSelectedCategory(filterValue || "");
      }

      setPage(1);
    },
    [setPage]
  );

  const handleResetFilters = useCallback(() => {
    setColumnFilters({});
    setSelectedWorkflowName("");
    setSelectedCategory("");
    setPage(1);
    setRowsPerPage(15);
  }, [setPage, setRowsPerPage]);

  const renderPagination = useCallback(() => {
    if (!workflows || error) return null;

    return (
      <CustomPagination
        page={page - 1} // Convert to 0-based for the pagination component
        rowsPerPage={rowsPerPage}
        totalCount={totalCount || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[5, 15, 30, 50, 100]}
      />
    );
  }, [
    page,
    rowsPerPage,
    totalCount,
    handlePageChange,
    handlePageSizeChange,
    workflows,
    error,
  ]);

  const actionButton = useMemo(
    () =>
      create_access ? (
        <ActionButton
          tooltipTitle="Define workflow"
          buttonText="Define workflow"
          path="/workflowDef"
        />
      ) : null,
    [create_access]
  );

  // Memoize gridOptions
  const gridOptions = useMemo(
    () => ({
      suppressCellFocus: false,
      animateRows: true,
      loadingOverlayComponent: "agGridLoadingOverlay",
      components: {
        agGridLoadingOverlay: CustomLoadingOverlay,
      },
      loadingOverlayComponentParams: {
        loadingMessage: "Loading Definitions...",
      },

      domLayout: "normal",
      suppressHorizontalScroll: false,
      suppressVerticalScroll: false,
    }),
    []
  );

  return (
    // make root a full–screen flex container
    <div
      className={classes.wrapper}
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        paddingBottom: "90px", // reserve space for the fixed pagination
        /* OR: height: "calc(100vh - 50px)" to shrink the content area */
      }}
    >
      {/* allow paper to grow and clip overflow */}
      <div
        className={classes.contentContainer}
        style={{
          flex: "1 1 auto",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Helmet>
          <title>Conductor UI - Workflow Definitions</title>
        </Helmet>
        <Header loading={isFetching} permissions={permissions} />

        {error && (
          <Alert severity="error" style={{ margin: "10px" }}>
            <AlertTitle>Error loading workflow definitions</AlertTitle>
            {error.message || "An unknown error occurred"}
          </Alert>
        )}

        {/* let this container flex–grow and scroll */}
        <div
          style={{
            flex: "1 1 auto",
            overflow: "auto", // enable internal scrollbar
          }}
        >
          <AgGridTable
            rowData={workflows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            defaultVisibleColumns={defaultVisibleColumns}
            initialTheme="alpine"
            handleRefreshClick={handleRefresh}
            loading={isFetching}
            error={error}
            currentFilters={columnFilters}
            onFilterChanged={handleColumnFilterChanged}
            resetServerFilters={true}
            handleResetFilters={handleResetFilters}
            gridOptions={gridOptions}
            tooltipShowDelay={500}
            enableRowSelection={false}
            actionButton={actionButton}
            isWorkflowContext={true}
          />
        </div>

        {ConformationDialog}

        {messageSnackbar && (
          <Snackbar
            {...messageSnackbar}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            action={
              <React.Fragment>
                <IconButton
                  aria-label="close"
                  color="inherit"
                  className={classes.close}
                  onClick={() => setMessageSnackbar(null)}
                >
                  <CloseIcon />
                </IconButton>
              </React.Fragment>
            }
          />
        )}
      </div>

      {workflows && !error && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            height: "50px",
            backgroundColor: "#fff",
            zIndex: 1000,
            boxShadow: "0px -2px 4px rgba(0,0,0,0.05)",
            padding: "0 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          {renderPagination()}
        </div>
      )}
    </div>
  );
}
